import { getConfig } from '@superbet-group/web.lib.config';
import {
    isBeforeDate,
    toStringDate,
    addHours,
    toDate,
} from '@superbet-group/web.lib.utilities';
import {
    BonusType,
    TransactionType,
} from '@superbet-group/web.types';
import { sumBy } from 'lodash';

import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import { BonusBalanceData } from '@core/models/user/BonusBalanceData';

import { RealMoneyBalanceData } from './RealMoneyBalance';
import User from './User';
import { getAccountConfig } from '@accountFederation/config/accountConfig';

export function shouldChooseGdprConsent(user: User): boolean {
    return isLegacyUser(user) && !hasGdprConsent(user);
}

export function hasGdprConsent(user: User): boolean {
    return user.acceptsGeneralPrivacy && user.acceptsCustomPrivacy;
}

export function isLegacyUser(user: User): boolean {
    return isBeforeDate(user.lastModified, new Date(2018, 5, 25));
}

export function getBalance(user: User): number | null {
    return user.balance?.realMoney?.total ?? null;
}

export function getWithdrawableBalance(user: User): number | null {
    const withdrawableCash = user.balance?.realMoney?.withdrawable;
    if (withdrawableCash !== null && withdrawableCash !== undefined) {
        return withdrawableCash;
    }

    return getBalance(user);
}

export function getWalletBreakdown(user: User): RealMoneyBalanceData | null {
    return user?.balance?.realMoney ?? null;
}

export function getBalanceWithBonus(user: User): number | null {
    if (!user.balance?.realMoney) {
        return null;
    }

    return user.balance.realMoney.total + getTotalBonusBalance(user)!;
}

export function getTotalBonusBalance(user: User): number {
    if (!user.balance) {
        return 0;
    }
    return sumBy(Object.values(BonusType), (type) => getBonusBalance(user, type));
}

export function getBonusBalanceData(user: User): BonusBalanceData | null {
    if (!user.balance) {
        return null;
    }

    return user.balance.bonuses;
}

export function getBonusBalance(user: User, type: keyof BonusBalanceData): number {
    const bonusBalanceData = getBonusBalanceData(user);

    if (!bonusBalanceData) {
        return 0;
    }

    return bonusBalanceData[type]?.amount || 0;
}

export function setIdCardVerificationPending(user: User): void {
    const expiryDate = getAccountConfig().isCanonKycEnabled && getAccountConfig().useKycLocalStoragePendingState ? 1 : undefined;
    clientStorageWithExpiry.set(`user:${user.userId}:idCardLastUpload`, toStringDate(new Date()), expiryDate);
}

export function deleteIdCardVerificationPending(user: User): void {
    clientStorageWithExpiry.remove(`user:${user.userId}:idCardLastUpload`);
}

export function isIdCardVerificationCheckRequired(user: User, transactionType: TransactionType): boolean {
    let idCardVerificationCheckStatus = false;
    const config = getConfig();

    switch (transactionType) {
        case TransactionType.Withdrawal:
            idCardVerificationCheckStatus = config.countrySpecific.user.withdrawal.idCardVerificationCheck.isRequired;
            break;
        case TransactionType.Deposit:
            idCardVerificationCheckStatus = config.countrySpecific.user.deposit.idCardVerificationCheck.isRequired;
            break;
    }

    return idCardVerificationCheckStatus ? !user.isVerifiedIdCard : false;
}

export function isIdCardVerificationPending(
    user: User,
    pendingHours = getConfig().app.user.idCardVerification.pendingHours,
): boolean {
    const lastUploadDate = clientStorageWithExpiry.get<string>(`user:${user?.userId}:idCardLastUpload`);

    if (!lastUploadDate) {
        return false;
    }

    const pendingEndDate = addHours(pendingHours, toDate(lastUploadDate));
    return isBeforeDate(new Date(), pendingEndDate);
}

export function isIdCardVerificationJustBeenApproved(user: User): boolean {
    return user && user.isVerifiedIdCard && isIdCardVerificationPending(user);
}

// Cookiebot clears our local storage, this is why we need their prefix
const INTRO_VIEWED = 'Necessary_INTRO_VIEWED';

export function isFirstTimeVisit() {
    // second condition is legacy
    return clientStorageWithExpiry.get(INTRO_VIEWED) !== true
        && clientStorageWithExpiry.get('INTRO_VIEWED') !== true;
}

export function markFirstVisit() {
    clientStorageWithExpiry.set(INTRO_VIEWED, true);
}

export function getSessionId(sessionString: string | null): string | null {
    if (!sessionString) {
        return null;
    }
    return sessionString.split('|')[0] || null;
}
